import { useEffect } from "react";
import NavBar from "@/components/site/NavBar";
import Hero from "@/components/site/Hero";
import AttorneyBar from "@/components/site/AttorneyBar";
import FearSection from "@/components/site/FearSection";
import FounderStory from "@/components/site/FounderStory";
import GoogleReviews from "@/components/site/GoogleReviews";
import Testimonials from "@/components/site/Testimonials";
import FinalCTA from "@/components/site/FinalCTA";
import Footer from "@/components/site/Footer";
import { useAbRedirect } from "@/lib/useAbRedirect";

const heroImage =
  "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=80&w=1200";

const headline = (
  <>
    Are you a victim of{" "}
    <span className="italic font-medium text-teal">domestic violence?</span>
  </>
);

const subhead = (
  <>
    You don&rsquo;t have to answer that question alone.{" "}
    <span className="text-ink font-medium">Binoye Jos, Esq.</span> and the JOS
    Family Law team handle domestic violence cases in Orange County with{" "}
    <span className="text-ink font-medium">complete discretion</span>,{" "}
    <span className="text-ink font-medium">same-week availability</span>, and{" "}
    <span className="text-ink font-medium">no judgment</span>. If you&rsquo;re
    in a dangerous situation at home, the right legal help — quickly and
    privately — can change everything.
  </>
);

const pullquote = (
  <>
    Help can be private. Help can be fast.{" "}
    <span className="italic text-teal">
      Reaching out doesn&rsquo;t commit you to anything — but it can open every
      door.
    </span>
  </>
);

// Stat bar intentionally omitted on DV — the numbers (filings, fees) feel
// out of place for a victim reading this page. The credibility comes from
// the founder story and testimonials below.
const statBar = null;

const fears = [
  "Is it safe to call an attorney right now?",
  "Will anyone find out I reached out for help?",
  "Can I get a restraining order quickly?",
  "What happens to my children if I leave?",
];

const founderHeading = (
  <>
    Binoye Jos, Esq. understands what it means to fight for your family{" "}
    <span className="italic">when everything feels uncertain.</span>
  </>
);

const founderParagraphs = [
  "Binoye Jos hired two prominent law firms and spent $400,000 fighting for his son. Both told him he'd never get equal custody. He fired them, represented himself — and won equal custody. Then he went to law school. He knows what fear looks like in a courtroom — and what it takes to move through it with a strategy that actually works.",
  "Every DV case at this firm is handled with discretion, urgency, and a plan that's built for your safety first.",
];

const dvTestimonials = [
  {
    name: "Ms. Valdez",
    quote:
      "This firm is not in the business of telling you what you want to hear. It is focused on what is best for your family and winning your case. Founded by someone who went through this himself, the level of empathy and legal experience is the kind most attorneys cannot match.",
  },
  {
    name: "Ms. Lee",
    quote:
      "Jos Family Law will fight for your case and what is fair. I went from a situation I thought was hopeless to one where I had real legal standing. I cannot recommend them enough.",
  },
  {
    name: "Mr. Kogami",
    quote:
      "If it wasn't for Jos Family Law, I would not have been able to come to a settlement that I felt was fair for both parties.",
  },
];

export default function DomesticViolence() {
  useAbRedirect(
    "/domestic-violence-attorney-orange-county-vsl",
    "/domestic-violence-attorney-orange-county-vsl-v2",
    "dv"
  );
  useEffect(() => {
    document.title =
      "Jos Family Law — Domestic Violence Attorney, Orange County CA";
    const meta =
      document.querySelector('meta[name="description"]') ||
      (() => {
        const m = document.createElement("meta");
        m.name = "description";
        document.head.appendChild(m);
        return m;
      })();
    meta.setAttribute(
      "content",
      "Private DV legal help in Orange County. Confidential, urgent, same-week availability. JOS Family Law handles domestic violence cases with complete discretion. Call (714) 733-7066."
    );
  }, []);

  return (
    <main data-testid="dv-page" className="relative bg-paper text-ink">
      <NavBar />
      <Hero
        source="DV"
        eyebrow="Family Law Attorney · Orange County, California"
        headline={headline}
        subhead={subhead}
        primaryCtaLabel="Get Confidential Help Now"
        ctaSubNote="Private · Confidential · No Obligation"
        statBar={statBar}
        heroImage={heroImage}
        imageCaption="First name only on the form is fine. We protect your privacy."
        pullquoteLabel="Our Promise"
        pullquote={pullquote}
        formSub="We'll reach out promptly and discreetly. First name only is fine."
        formCtaLabel="Get Confidential Help Now"
        showPriorResultsDisclaimer={false}
      />
      <AttorneyBar />
      <FearSection
        eyebrow="The Quiet Hours"
        heading={
          <>
            The questions{" "}
            <span className="italic font-medium">keeping you up at night.</span>
          </>
        }
        fears={fears}
      />
      <FounderStory heading={founderHeading} paragraphs={founderParagraphs} />
      <GoogleReviews />
      <Testimonials
        items={dvTestimonials}
        heading={
          <>
            Satisfied <span className="italic">clients.</span>
          </>
        }
        clientLabel="Family Law Client"
      />
      <FinalCTA source="DV" />
      <Footer />
    </main>
  );
}
